import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, msalInstance } from './AuthProvider';
import { isNative, platform } from '../capacitor';
import { captureError } from '../services/posthog';
import { completeNativeMicrosoftLogin, isNativeMicrosoftAuthResponse } from '../services/nativeMsAuth';

const MicrosoftCallback: React.FC = () => {
  const navigate = useNavigate();
  const { completeLogin } = useAuth();

  useEffect(() => {
    handleCallback();
  }, []);

  const handleCallback = async () => {
    if (window.opener && window.opener !== window) return;

    // If we're inside MSAL's hidden silent-renewal iframe, MSAL itself consumes the
    // response from the parent frame. The full app should never boot here, and any
    // navigate() would attempt a cross-origin frame navigation the browser blocks
    // ("unsafe attempt to initiate navigation") — so bail and let MSAL finish.
    if (window.parent !== window) return;

    // Home is the post-sign-in landing page. Only the calendar's own Outlook-reconnect
    // flows return somewhere else, and each of those records where it came from in
    // msalReturnTo (read below) rather than relying on this default.
    let returnTo = '/';
    try {
      // The Capacitor code+PKCE flow returns its authorization code in the URL fragment. MSAL's
      // handleRedirectPromise won't recognize it (the request was made by nativeMsAuth, not
      // MSAL), so nativeMsAuth redeems the code and cross-checks the state itself.
      if (isNativeMicrosoftAuthResponse(window.location.hash)) {
        const claims = await completeNativeMicrosoftLogin(window.location.hash);
        window.location.hash = '';

        if (claims) {
          completeLogin({ ...claims, provider: 'microsoft' });
          // A re-authentication triggered mid-session (apiAuth) records where the user was, so
          // an expiry that interrupted them returns them there instead of to the home page.
          const nativeReturnTo = sessionStorage.getItem('msalReturnTo');
          if (nativeReturnTo) sessionStorage.removeItem('msalReturnTo');
          navigate(nativeReturnTo || '/', { replace: true });
          return;
        }
        // A response we couldn't prove was ours (stale or forged). Send the user home rather
        // than on to the MSAL path, which has nothing to do with this session.
        navigate('/', { replace: true });
        return;
      }

      await msalInstance.initialize();
      const result = await msalInstance.handleRedirectPromise();

      // Mark the just-authenticated account active so subsequent silent token acquisition
      // resolves it and uses the refresh token, rather than falling back to a hidden
      // iframe. handleRedirectPromise returns the account on a fresh login; otherwise pick
      // up whatever's already cached.
      const account = result?.account ?? msalInstance.getAllAccounts()[0];
      if (account) msalInstance.setActiveAccount(account);

      const stored = sessionStorage.getItem('msalReturnTo');
      if (stored) {
        returnTo = stored;
        sessionStorage.removeItem('msalReturnTo');
      }
    } catch (e) {
      console.error('[MicrosoftCallback] error:', e);
      captureError(e, { component: 'MicrosoftCallback', isNative, platform });
    }

    navigate(returnTo, { replace: true });
  };

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      backgroundColor: '#0a202e',
      color: '#20cbd4'
    }}>
      <div>
        <h2>Completing sign in...</h2>
        <p>Please wait while we redirect you.</p>
      </div>
    </div>
  );
};

export default MicrosoftCallback;
